import {
  Component, DestroyRef, inject,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {Website} from "../website/website";
import {Router} from '@angular/router';
import {DialogService} from '../../../ng2-bootstrap-modal/dialog.service';
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from 'rxjs/operators';
import {DbResponse} from '../../shared/interceptors/db-response';
import {Utils} from '../../shared/utils/utils';
import {Product} from "../website/product";
import {DbStoreState} from "../../store";
import {Store} from "@ngrx/store";
import * as fromStore from "../../store";
import {Config} from "../../config/config";
import {BuilderTemplates} from "../website/builder-templates/builder-templates";
import {IActivity} from "../../activities/activity";
import {Observable} from "rxjs";
import {BuilderTemplatesService} from "../website/builder-templates/builder-templates.service";
import {User} from "../../user/user";
import {UserSubscription} from "../../user/user-subscription";
import {GoogleAnalyticsService} from "../../shared/services/google-analytics/google-analytics.service";
import {Subject} from "rxjs";
import {clientRoles} from "../../user/user-roles-actions";
import {CookieService} from "ngx-cookie";
import {TypeUtils} from "../../shared/utils/type-utils";
import {combineLatest} from "rxjs";
import {
  IAIBuilderPage
} from "../website/ai-builder/ai-builder-pages/ai-builder-pages-table/ai-builder-pages-table.component";
import {DbModalService} from "../../modules/modal/modal";
import {
  DemoProgressDoneComponent
} from "../../shared/components/dialogs/account-upgraded-dialog/demo-progress-done/demo-progress-done.component";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {GlobalEvent} from "../../shared/services/global-event-dispatcher/global-event";
import {
  GlobalEventDispatcherService
} from "../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {OneMonthFreeComponent} from "../../shared/components/dialogs/one-month-free/one-month-free.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'db-website-item-menu',
  templateUrl: './website-item-menu.component.html',
  styleUrls: ['./website-item-menu.component.scss'],
  standalone: false
})
export class WebsiteItemMenuComponent implements OnInit, OnChanges, OnDestroy {

  @Input() website: Website;
  @Input() user: User;
  @Input() currentSubscription: UserSubscription;
  @Input() inline: boolean;

  @Input() hideManageButton: boolean;
  @Input() parentWebsite: Website;
  @Input() networkSubsites: Website[];

  public changingEnvironment: boolean = false;
  public websiteSettingsLoading: boolean;
  public devEnvironmentEnabled: boolean;
  public siteUrl: string;
  public builderPlugin: Product;
  public elementorPlugin: Product;
  public lastTemplateItem: BuilderTemplates;
  public hasLastImportedTemplate: boolean = false;
  public websiteScore: any;
  public mobileScore: number;
  public ongoingGoogleScoreActivity$: Observable<IActivity>;
  public pages: IAIBuilderPage[] | null = null;
  public homePage: IAIBuilderPage;
  public hasFrontPage: boolean = true;

  public subsitesToggled: boolean;
  public showSubsiteList: boolean = true;
  public childWebsiteId: any;
  public hasPasswordProtection: boolean;
  public passwordProtectionPassword: any;
  public passwordProtectionFirstTimeClicked: boolean;
  public passwordProtectionFirstTimeClickedOverlay: boolean;
  public loading: boolean;
  public radius: number = 2 * 3.14 * 18;
  public stagingAccessOnly: boolean;
  public workspaceRoles = clientRoles;
  public dbTesting: boolean;
  public regenerationCount: number = 0;
  public readonly defaultStarterPlanId = Config.defaultStarterPlanId;
  public readonly basePlanId = Config.basePlanId;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private currentWorkspaceRole: number;
  public previewUrl: string;
  public redirectUrl: string;
  public demoUser: boolean;
  isMobile: boolean = false;
  needRedirectToRegenerate = false;
  config = Config;

  private _destroyRef = inject(DestroyRef);

  constructor(
      private _router: Router,
      private _dialogService: DialogService,
      private _store: Store<DbStoreState>,
      private _builderTemplatesService: BuilderTemplatesService,
      private _googleAnalyticsService: GoogleAnalyticsService,
      private _cookieService: CookieService,
      private _globalEventDispatcher: GlobalEventDispatcherService,
      private _modalService: DbModalService,
  ) {
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 768;
    this.demoUser = Utils.isDemoUser(this.currentSubscription, this.user);
    if (this.website && this.currentSubscription) {
      const redirectPath = this.website?.isWVC ? 'admin.php?page=wvc-editor' : '?edit.php?post_type=page&from=tenweb_dashboard&open=homepage';
      this.redirectUrl = this.website?.adminUrl + redirectPath;
    }
    this.dbTesting = !!this._cookieService.get('db-testing');
    this.previewUrl = this.demoUser ? `${this.website.site_url}?from=sidebar&client_id=${this.user.id}&domain_id=${Config.demoDomainId}&first_time` + (this.dbTesting ? `&db-testing` : ``) : this.website.site_url;
    this.childWebsiteId = this.website.isNetwork && !this.website.parentId && this.website.children &&
    this.website.children.length ? this.website.children[0] : null;
    this.siteUrl = this.website.site_url.replace(/(^\w+:|^)\/\//, '');
    this.ongoingGoogleScoreActivity$ = this._store.select(fromStore.getGoogleScoreOngoingActivity(this.website.id));
    this._getUserData();
    this._getData();
    this._getWebsiteData();
    this._getLastImportedTemplate();
    this._setStagingAccessOnly();
    if (this.website?.isWVC) {
      this._listenVibeFiles();
      this._store.dispatch(new fromStore.LoadVibeFiles({domainId: this.website.id}));
    }
  }

  private _listenVibeFiles() {
    this._store.select(fromStore.getVibeFilesStatus(this.website.id))
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(value => {
          this.needRedirectToRegenerate = value === 'fail';
        });
  }

  private _setStagingAccessOnly() {
    this._store.select(fromStore.getWebsitesLoaded)
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => combineLatest(
                this._store.select(fromStore.getWebsites),
                this._store.select(fromStore.getWebsite(this.website.id))
            )),
            first()
        )
        .subscribe(([websites, website]) => {
          this.website = website;
          const redirectPath = this.website?.isWVC ? 'admin.php?page=wvc-editor' : '?edit.php?post_type=page&from=tenweb_dashboard&open=homepage';
          this.redirectUrl = this.website?.adminUrl + redirectPath;
          this.previewUrl = this.demoUser ? `${this.website.site_url}?from=sidebar&client_id=${this.user.id}&domain_id=${Config.demoDomainId}&first_time` + (this.dbTesting ? `&db-testing` : ``) : this.website.site_url;
          this.stagingAccessOnly = this.website.type === 'staging' ? !websites.find((website) => website.id === this.website.siblingId.live) : false;
        });

    this._store.select(fromStore.getUser)
        .pipe(
            takeUntil(this._destroyStream$),
            filter(user => !!user)
        )
        .subscribe(user => this.user = user);

    this._store.select(fromStore.getUserLoaded)
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => this._store.select(fromStore.getWorkspaceIdEntities)),
            distinctUntilChanged((prev, curr) => prev.currentWorkspaceRoleId === curr.currentWorkspaceRoleId),
            takeUntil(this._destroyStream$)
        )
        .subscribe((entities) => {
          this.currentWorkspaceRole = entities && entities.currentWorkspaceRoleId;
        });

    this._store.select(fromStore.getCreatedPages(this.website.id))
        .subscribe((pages) => {
          this._getPagesList(pages);
        });

    this._store.select(fromStore.getRegenerationCount(this.website.id))
        .pipe(
            filter(count => !!count)
        )
        .subscribe(count => {
          this.regenerationCount = count;
        });
  }

  private _getPagesList(pages): void {
    if (pages && TypeUtils.isDefined(pages) && !TypeUtils.isEmpty(pages)) {
      this.pages = pages.filter((page) => page && page.pageType === 'page');
      let frontPage = pages.find(page => page && !!page.frontPage);
      this.hasFrontPage = !!frontPage;
      this.homePage = frontPage;
    }
  }

  public toggle() {
    this.subsitesToggled = !this.subsitesToggled;
  }

  public gotToSite(websiteId) {
    this.subsitesToggled = false;
    this.showSubsiteList = false;
    setTimeout(() => {
      this.showSubsiteList = true;
    }, 1000);
    this._router.navigate(['/websites/' + websiteId + '/change-environment/network'],
        {skipLocationChange: true, replaceUrl: true});
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update demoUser status if user or subscription change
    if (changes['user'] || changes['currentSubscription']) {
      this.demoUser = Utils.isDemoUser(this.currentSubscription, this.user);
    }

    if (changes['website']) {
      this.siteUrl = this.website && this.website.site_url && this.website.site_url.replace(/(^\w+:|^)\/\//, '');
      this.previewUrl = this.demoUser ? `${this.website.site_url}?from=sidebar&client_id=${this.user.id}&domain_id=${Config.demoDomainId}&first_time` + (this.dbTesting ? `&db-testing` : ``) : this.website.site_url;
      this.websiteScore =
          this.website.score && this.website.score.currentScore && this.website.score.currentScore.desktopScore;
      this.mobileScore =
          this.website.score && this.website.score.currentScore && this.website.score.currentScore.mobileScore;

      this.changingEnvironment = false;
      this._getData();
      this._getWebsiteData();
    }
  }

  private _getUserData(): void {
    this._store.select(fromStore.getUserLoaded)
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => this._store.select(fromStore.getUserSubscription)),
            takeUntil(this._destroyStream$)
        )
        .subscribe((userSubscription: UserSubscription) => {
          this.currentSubscription = userSubscription;
          this.demoUser = Utils.isDemoUser(userSubscription, this.user);
          if (this.website && this.website.site_url) {
            this.previewUrl = this.demoUser ?
                `${this.website.site_url}?from=sidebar&client_id=${this.user.id}&domain_id=${Config.demoDomainId}&first_time` + (this.dbTesting ? `&db-testing` : ``) :
                this.website.site_url;
          }
        });
  }

  private _getData(): void {
    this._store.select(fromStore.getWebsitePluginsLoaded(this.website?.id))
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => combineLatest(
                this._store.select(fromStore.getWebsiteElementorPlugin(this.website?.id)),
                this._store.select(fromStore.getWebsiteBuilderPlugin(this.website?.id))
            )),
            distinctUntilChanged(([elementorOld, builderOld], [elementorNew, builderNew]) => elementorOld && elementorNew && builderOld && builderNew && elementorOld.active === elementorNew.active && builderOld.active === builderNew.active),
            takeUntil(this._destroyStream$)
        )
        .subscribe(([elementorProduct, builderProduct]) => {
          if (elementorProduct) {
            this.elementorPlugin = elementorProduct;
          }
          if (builderProduct) {
            this.builderPlugin = builderProduct;
            this._builderOngoingActivity();
          }
          if (this.website && (!this.website.isNetwork || (this.website.isNetwork && this.website.parentId))
              && this.elementorPlugin && this.elementorPlugin.active && this.builderPlugin && this.builderPlugin.active) {
            this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.website.id));
          }
        });
  }

  private _builderOngoingActivity(): void {
    this._store.select(fromStore.getBuilderOngoingActivity(this.website.id, this.builderPlugin.slug, !!this.website.domain_hash))
        .subscribe((activity) => {
          if (activity) {
            if (activity.status === 'done' && !this.hasLastImportedTemplate) {
              this._getLastImportedTemplate();
            }
          }
        });
  }

  private _getWebsiteData(): void {
    /*TODO CHECK BOOSTER HOSTED WEBSITES AND ASK HRACH*/
    if (this.website && this.website.domain_hash) {
      this.websiteSettingsLoading = true;

      this.getWebsiteSettings();
      this._store.dispatch(new fromStore.LoadInfo(this.website.id));
    } else {
      this.devEnvironmentEnabled = false;
    }
  }

  private _getLastImportedTemplate(): void {
    this._builderTemplatesService.getLastBuilderTemplate(this.website.id)
        .pipe(
            filter((response: DbResponse): boolean => !!response),
            first()
        )
        .subscribe((lastImportedTemplate): void => {
          if (lastImportedTemplate && lastImportedTemplate.data && (lastImportedTemplate.data.id ||
              lastImportedTemplate.data.type === "last_recreated")) {
            this.hasLastImportedTemplate = true;
            this.lastTemplateItem = lastImportedTemplate.data;
          }

        });
  }

  public redirectToInner(websiteId) {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: "MoveTo10Web Page visit",
      eventLabel: 'Score click menu-thumb',
      websiteId: this.website && this.website.id
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);

    if (TypeUtils.isDefined(this.currentSubscription.plan.services.speed_optimizer)) {
      this._router.navigateByUrl(`/websites/${this.website.id}/booster/frontend`);
    } else {
      this._router.navigateByUrl(`/websites/${this.website.id}/move-to-10web`);
    }
  }

  /*  PasswordProtectionDialog
    public togglePasswordProtectionDialog(toggle) {

      if (this.isMobile) {
        this.mobileAvailabilityPassword();
      }
      else {
        this.showPasswordProtectionDialog = toggle;
      }
    }

    public disablePasswordProtection() {
      if (!this.loading && this.website) {
        this.loading = true;
        this._hostingService.disablePasswordProtection(this.website.id)
          .pipe(
            filter((response: DbResponse): boolean => !!response),
            first()
          )
          .subscribe((response: DbResponse): void => {
            if (response.success) {
              const message = response.message === 'successfully disabled' ? 'successfully enabled' : response.message;
              NotifierComponent.notify(new Notification(message, NotificationType.SUCCESS));
              this.getWebsiteSettings();
              this.passwordProtectionDisabled = response.message === 'successfully disabled';
            }
            else {
              NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
              this.loading = false;
            }
          });
      }

    }

    public copiedPasswordProtectionUsername() {
      this.isCopiedPasswordProtectionUsername = true;
      setTimeout( () => this.isCopiedPasswordProtectionUsername = false, 800);
    }

    public copiedPasswordProtectionPassword() {
      this.isCopiedPasswordProtectionPassword = true;
      setTimeout( () => this.isCopiedPasswordProtectionPassword = false, 800);
    }

    PasswordProtectionDialog
    public mobileAvailabilityPassword() {
        this._dialogService.addDialog(MobileAvailableComponent, {
          header: 'Website Visible From<br/>Your IP Only',
          message: 'Please use Desktop version to enable<br/>public visibility and to allow access to<br/>other IP addresses.'
        });
      }

    private _setCurrentWorkspaceRole() {
      this._store.select(fromStore.getUserLoaded)
        .pipe(
          filter((loaded: boolean) => loaded),
          switchMap(() => this._store.select(fromStore.getWorkspaceIdEntities)),
          distinctUntilChanged((prev, curr) => prev.currentWorkspaceRoleId === curr.currentWorkspaceRoleId),
          takeUntil(this._destroyStream$)
        )
        .subscribe((entities) => {
          this.currentWorkspaceRole = entities && entities.currentWorkspaceRoleId;
        });
    }
    */

  private getWebsiteSettings() {
    const websiteId = this.website.isNetwork && !this.website.parentId && this.website.children
    && this.website.children.length ? this.website.children[0] : this.website.id;
    this._store.dispatch(new fromStore.LoadHostingSettings(websiteId));
    this._store.select(fromStore.getHostingSettingsLoaded(websiteId))
        .pipe(
            filter((loaded: boolean) => !!loaded),
            switchMap(() => this._store.select(fromStore.getHostingSettings(websiteId))),
            first()
        )
        .subscribe((response: any) => {
          this.websiteSettingsLoading = false;
          this.devEnvironmentEnabled = Utils.isTruthy(response.staging_enabled);
          // PasswordProtection
          // if (response.password_protection) {
          //   this.canDisablePasswordProtection = Utils.isTruthy(response.password_protection.can_disable);
          //   this.passwordProtectionDisabled = Utils.isTruthy(response.password_protection.disabled);
          // }
        });
  }

  public sendEvent(from, action = null) {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: action || 'Website Admin',
      eventLabel: from,
      websiteId: this.website && this.website.id
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public showCheckoutDialog(from: 'feature' | 'editor' | 'wp-admin'): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: "Generative AI - Locked action",
      eventLabel: from === 'editor' ? "Edit Website" : "WP Admin"
    };
    if (from === 'editor' && this.user && !this.user.agencyOrFreelancer) {
      analyticsData.eventLabel += " - SMB";
    }
    if (this.demoUser) {
      const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
      const options = {
        overflowHidden: false,
        backdropClass: 'blurred',
      };
      if (!firstTime) {
        options['width'] = 1240;
      } else {
        options['width'] = 460;
        options['height'] = 600;
      }
      const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
      modal.componentInstance.showFirstTime = firstTime;

      // Update demoUser status when modal is closed (user might have upgraded)
      modal.closed.subscribe(() => {
        // Fetch latest subscription data and update demoUser flag
        this._store.select(fromStore.getUserSubscription)
            .pipe(first())
            .subscribe(subscription => {
              this.currentSubscription = subscription;
              this.updateDemoUserStatus();
            });
      });

      // Also handle dismissed event
      modal.dismissed.subscribe(() => {
        this._store.select(fromStore.getUserSubscription)
            .pipe(first())
            .subscribe(subscription => {
              this.currentSubscription = subscription;
              this.updateDemoUserStatus();
            });
      });
    } else {
      const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
      modal.componentInstance.mixed = from === 'editor' && this.demoUser;
      modal.componentInstance.mode = from;

      // Update demoUser status when modal is closed (user might have changed subscription)
      modal.closed.subscribe(() => {
        // Fetch latest subscription data and update demoUser flag
        this._store.select(fromStore.getUserSubscription)
            .pipe(first())
            .subscribe(subscription => {
              this.currentSubscription = subscription;
              this.updateDemoUserStatus();
            });
      });

      // Also handle dismissed event
      modal.dismissed.subscribe(() => {
        this._store.select(fromStore.getUserSubscription)
            .pipe(first())
            .subscribe(subscription => {
              this.currentSubscription = subscription;
              this.updateDemoUserStatus();
            });
      });
    }
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  handleEditClick(): void {
    if (this.website?.isWVC && this.needRedirectToRegenerate) {
      if (this._globalEventDispatcher.vibeGenerateDialog$.value) {
        this._globalEventDispatcher.vibeGenerateDialog$.value();
        return;
      }
    }
    if (this.isMobile) {
      event.preventDefault();
      // Open dialog for mobile
      const modal = this._modalService.open(DemoProgressDoneComponent, {hasCloseBtn: true});
      modal.componentInstance.forMobileEdit = true;
      modal.componentInstance.websiteId = this.website?.id;
      modal.componentInstance.user = this.user;
    }
  }

  /**
   * Updates the demoUser flag based on current user and subscription
   * Call this method whenever you need to refresh the demoUser status
   */
  public updateDemoUserStatus(): void {
    this.demoUser = Utils.isDemoUser(this.currentSubscription, this.user);
    // Update previewUrl based on new demoUser status
    if (this.website && this.website.site_url) {
      this.previewUrl = this.demoUser ?
          `${this.website.site_url}?from=sidebar&client_id=${this.user.id}&domain_id=${Config.demoDomainId}&first_time` + (this.dbTesting ? `&db-testing` : ``) :
          this.website.site_url;
    }
  }

  showDemoIframe(): void {
    this._globalEventDispatcher.dispatchEvent(GlobalEvent.showDemoPreviewIframe, {
      showIframe: true,
      iframeUrl: this.previewUrl
    });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }
}
